package org.example.project.client.api

import io.ktor.client.HttpClient
import io.ktor.client.plugins.HttpRequestTimeoutException
import io.ktor.client.request.header
import io.ktor.client.request.request
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import org.example.project.client.config.ApiConfig
import org.example.project.client.error.ApiClientException
import org.example.project.client.error.ConflictException
import org.example.project.client.error.ForbiddenException
import org.example.project.client.error.MissingCsrfTokenException
import org.example.project.client.error.NetworkException
import org.example.project.client.error.RateLimitedException
import org.example.project.client.error.RequestTimeoutException
import org.example.project.client.error.ServerErrorException
import org.example.project.client.error.UnauthorizedException
import org.example.project.client.error.UnexpectedPayloadException
import org.example.project.client.error.ValidationException
import org.example.project.client.model.ApiErrorResponse
import org.example.project.client.model.AuthResponseAndroid
import org.example.project.client.model.AuthResponseWeb
import org.example.project.client.model.ClientType
import org.example.project.client.model.LoginRequest
import org.example.project.client.model.LogoutResponse
import org.example.project.client.model.RegisterRequest
import org.example.project.client.model.UpdateProfileRequest
import org.example.project.client.model.UserProfileResponse

sealed interface LoginApiResponse {
    data class Android(val response: AuthResponseAndroid) : LoginApiResponse
    data class Web(val response: AuthResponseWeb) : LoginApiResponse
}

interface AuthApiClient {
    suspend fun register(request: RegisterRequest): UserProfileResponse
    suspend fun login(request: LoginRequest): LoginApiResponse
    suspend fun getMe(bearerToken: String? = null): UserProfileResponse
    suspend fun updateProfile(
        displayName: String,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): UserProfileResponse
    suspend fun logout(
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): LogoutResponse
}

internal class DefaultAuthApiClient(
    private val httpClient: HttpClient,
    private val apiConfig: ApiConfig,
    private val json: Json = Json {
        ignoreUnknownKeys = true
        isLenient = false
    }
) : AuthApiClient {

    override suspend fun register(request: RegisterRequest): UserProfileResponse {
        val endpoint = apiConfig.resolve("/auth/register")
        val response = executeRequest(
            url = endpoint,
            method = HttpMethod.Post,
            body = request
        )
        return decodeBody(response)
    }

    override suspend fun login(request: LoginRequest): LoginApiResponse {
        val endpoint = apiConfig.resolve("/auth/login")
        val response = executeRequest(
            url = endpoint,
            method = HttpMethod.Post,
            body = request
        )
        return when (request.clientType) {
            ClientType.ANDROID -> {
                val parsed = decodeBody<AuthResponseAndroid>(response)
                LoginApiResponse.Android(parsed)
            }
            ClientType.WEB -> {
                val parsed = decodeBody<AuthResponseWeb>(response)
                LoginApiResponse.Web(parsed)
            }
        }
    }

    override suspend fun getMe(bearerToken: String?): UserProfileResponse {
        val endpoint = apiConfig.resolve("/me")
        val headers = mutableMapOf<String, String>()
        if (!bearerToken.isNullOrBlank()) {
            headers[HttpHeaders.Authorization] = "Bearer $bearerToken"
        }
        val response = executeRequest(
            url = endpoint,
            method = HttpMethod.Get,
            headers = headers
        )
        return decodeBody(response)
    }

    override suspend fun updateProfile(
        displayName: String,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): UserProfileResponse {
        if (isWeb && csrfToken.isNullOrBlank()) {
            throw MissingCsrfTokenException()
        }
        val endpoint = apiConfig.resolve("/me")
        val headers = mutableMapOf<String, String>()
        if (!bearerToken.isNullOrBlank()) {
            headers[HttpHeaders.Authorization] = "Bearer $bearerToken"
        }
        if (isWeb && !csrfToken.isNullOrBlank()) {
            headers["X-CSRF-Token"] = csrfToken
        }
        val response = executeRequest(
            url = endpoint,
            method = HttpMethod.Patch,
            headers = headers,
            body = UpdateProfileRequest(displayName = displayName)
        )
        return decodeBody(response)
    }

    override suspend fun logout(
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): LogoutResponse {
        if (isWeb && csrfToken.isNullOrBlank()) {
            throw MissingCsrfTokenException("Missing CSRF token for web logout")
        }
        val endpoint = apiConfig.resolve("/auth/logout")
        val headers = mutableMapOf<String, String>()
        if (!bearerToken.isNullOrBlank()) {
            headers[HttpHeaders.Authorization] = "Bearer $bearerToken"
        }
        if (isWeb && !csrfToken.isNullOrBlank()) {
            headers["X-CSRF-Token"] = csrfToken
        }
        val response = executeRequest(
            url = endpoint,
            method = HttpMethod.Post,
            headers = headers
        )
        return decodeBody(response)
    }

    private suspend fun executeRequest(
        url: String,
        method: HttpMethod,
        headers: Map<String, String> = emptyMap(),
        body: Any? = null
    ): HttpResponse {
        require(apiConfig.isSameOrigin(url)) {
            "Request URL violates origin boundary of configured API base URL"
        }

        val httpResponse: HttpResponse = try {
            httpClient.request(url) {
                this.method = method
                if (body != null) {
                    contentType(ContentType.Application.Json)
                    setBody(body)
                }
                headers.forEach { (name, value) ->
                    header(name, value)
                }
            }
        } catch (e: CancellationException) {
            throw e
        } catch (_: HttpRequestTimeoutException) {
            throw RequestTimeoutException()
        } catch (e: Exception) {
            val msg = e.message ?: ""
            if (msg.contains("timeout", ignoreCase = true)) {
                throw RequestTimeoutException()
            }
            throw NetworkException()
        }

        checkStatusAndThrow(httpResponse)
        return httpResponse
    }

    private suspend fun checkStatusAndThrow(response: HttpResponse) {
        val status = response.status
        if (status.value in 200..299) {
            return
        }

        val requestIdHeader = response.headers[HttpHeaders.XRequestId] ?: "unknown"
        val bodyText = try {
            response.bodyAsText()
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            ""
        }

        val errorResponse = try {
            if (bodyText.isNotBlank()) json.decodeFromString<ApiErrorResponse>(bodyText) else null
        } catch (_: Exception) {
            null
        }

        val errorCode = errorResponse?.error ?: when (status) {
            HttpStatusCode.BadRequest -> "BAD_REQUEST"
            HttpStatusCode.Unauthorized -> "UNAUTHORIZED"
            HttpStatusCode.Forbidden -> "FORBIDDEN"
            HttpStatusCode.Conflict -> "CONFLICT"
            HttpStatusCode.TooManyRequests -> "TOO_MANY_REQUESTS"
            else -> "HTTP_${status.value}"
        }
        val errorMessage = errorResponse?.message ?: "HTTP ${status.value}"
        val reqId = errorResponse?.requestId ?: requestIdHeader

        when (status) {
            HttpStatusCode.BadRequest -> throw ValidationException(status.value, errorCode, errorMessage, reqId)
            HttpStatusCode.Unauthorized -> throw UnauthorizedException(status.value, errorCode, errorMessage, reqId)
            HttpStatusCode.Forbidden -> throw ForbiddenException(status.value, errorCode, errorMessage, reqId)
            HttpStatusCode.Conflict -> throw ConflictException(status.value, errorCode, errorMessage, reqId)
            HttpStatusCode.TooManyRequests -> {
                val retryAfter = response.headers[HttpHeaders.RetryAfter]?.toLongOrNull()
                throw RateLimitedException(status.value, errorCode, errorMessage, reqId, retryAfter)
            }
            else -> {
                if (status.value >= 500) {
                    throw ServerErrorException(status.value, errorCode, errorMessage, reqId)
                }
                throw ValidationException(status.value, errorCode, errorMessage, reqId)
            }
        }
    }

    private suspend inline fun <reified T> decodeBody(response: HttpResponse): T {
        val text = try {
            response.bodyAsText()
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            throw UnexpectedPayloadException()
        }

        return try {
            json.decodeFromString<T>(text)
        } catch (_: SerializationException) {
            throw UnexpectedPayloadException()
        }
    }
}
